export type CachedThreadSurface = "chat" | "work";

export type CachedThreadHistory = {
  thread: Record<string, unknown>;
  updatedAt: number;
};

export type CachedThreadList = {
  items: Record<string, unknown>[];
  updatedAt: number;
};

type CacheRecord = {
  key: string;
  scopePrincipal: string;
  kind: "thread" | "list";
  surface: CachedThreadSurface;
  threadId: string;
  updatedAt: number;
  value: Record<string, unknown> | Record<string, unknown>[];
};

const DATABASE_NAME = "bianhua-thread-history-v1";
const STORE_NAME = "entries";
const MAX_THREADS_PER_ACCOUNT = 100;

function cloned<T>(value: T): T | null {
  try {
    return JSON.parse(JSON.stringify(value)) as T;
  } catch {
    return null;
  }
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.addEventListener("success", () => resolve(request.result), { once: true });
    request.addEventListener("error", () => reject(request.error || new Error("IndexedDB request failed")), { once: true });
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.addEventListener("complete", () => resolve(), { once: true });
    transaction.addEventListener("abort", () => reject(transaction.error || new Error("IndexedDB transaction aborted")), { once: true });
    transaction.addEventListener("error", () => reject(transaction.error || new Error("IndexedDB transaction failed")), { once: true });
  });
}

export class ThreadHistoryPersistence {
  private readonly scope: string;
  private readonly memory = new Map<string, CacheRecord>();
  private databasePromise: Promise<IDBDatabase | null> | null = null;

  constructor(scope: string) {
    this.scope = scope;
  }

  private scopePrincipal(principalId: string): string {
    return `${this.scope}:${principalId}`;
  }

  private threadKey(principalId: string, surface: CachedThreadSurface, threadId: string): string {
    return `${this.scopePrincipal(principalId)}:thread:${surface}:${threadId}`;
  }

  private listKey(principalId: string, surface: CachedThreadSurface): string {
    return `${this.scopePrincipal(principalId)}:list:${surface}`;
  }

  private database(): Promise<IDBDatabase | null> {
    if (this.databasePromise) return this.databasePromise;
    this.databasePromise = new Promise((resolve) => {
      if (typeof indexedDB === "undefined") {
        resolve(null);
        return;
      }
      let request: IDBOpenDBRequest;
      try {
        request = indexedDB.open(DATABASE_NAME, 1);
      } catch {
        resolve(null);
        return;
      }
      request.addEventListener("upgradeneeded", () => {
        const database = request.result;
        const store = database.objectStoreNames.contains(STORE_NAME)
          ? request.transaction!.objectStore(STORE_NAME)
          : database.createObjectStore(STORE_NAME, { keyPath: "key" });
        if (!store.indexNames.contains("scopePrincipal")) store.createIndex("scopePrincipal", "scopePrincipal");
      });
      request.addEventListener("success", () => resolve(request.result), { once: true });
      request.addEventListener("error", () => resolve(null), { once: true });
      request.addEventListener("blocked", () => resolve(null), { once: true });
    });
    return this.databasePromise;
  }

  private async get(key: string): Promise<CacheRecord | null> {
    const memory = this.memory.get(key);
    if (memory) return cloned(memory);
    const database = await this.database();
    if (!database) return null;
    try {
      const record = await requestResult(database.transaction(STORE_NAME, "readonly").objectStore(STORE_NAME).get(key)) as CacheRecord | undefined;
      if (!record) return null;
      this.memory.set(key, record);
      return cloned(record);
    } catch {
      return null;
    }
  }

  private async put(record: CacheRecord): Promise<void> {
    const safe = cloned(record);
    if (!safe) return;
    this.memory.set(record.key, safe);
    const database = await this.database();
    if (!database) return;
    try {
      const transaction = database.transaction(STORE_NAME, "readwrite");
      transaction.objectStore(STORE_NAME).put(safe);
      await transactionDone(transaction);
    } catch {
      // The in-memory copy remains usable when persistent browser storage is unavailable.
    }
  }

  async getThread(principalId: string, surface: CachedThreadSurface, threadId: string): Promise<CachedThreadHistory | null> {
    const record = await this.get(this.threadKey(principalId, surface, threadId));
    if (!record || record.kind !== "thread" || Array.isArray(record.value)) return null;
    return { thread: record.value, updatedAt: record.updatedAt };
  }

  async setThread(principalId: string, surface: CachedThreadSurface, threadId: string, thread: Record<string, unknown>): Promise<void> {
    await this.put({
      key: this.threadKey(principalId, surface, threadId),
      scopePrincipal: this.scopePrincipal(principalId),
      kind: "thread",
      surface,
      threadId,
      updatedAt: Date.now(),
      value: thread,
    });
    void this.prune(principalId);
  }

  async deleteThread(principalId: string, surface: CachedThreadSurface, threadId: string): Promise<void> {
    const key = this.threadKey(principalId, surface, threadId);
    this.memory.delete(key);
    const database = await this.database();
    if (!database) return;
    try {
      const transaction = database.transaction(STORE_NAME, "readwrite");
      transaction.objectStore(STORE_NAME).delete(key);
      await transactionDone(transaction);
    } catch {
      // Recoverable: the server remains authoritative and a later write replaces the entry.
    }
  }

  async getList(principalId: string, surface: CachedThreadSurface): Promise<CachedThreadList | null> {
    const record = await this.get(this.listKey(principalId, surface));
    if (!record || record.kind !== "list" || !Array.isArray(record.value)) return null;
    return { items: record.value, updatedAt: record.updatedAt };
  }

  async setList(principalId: string, surface: CachedThreadSurface, items: Record<string, unknown>[]): Promise<void> {
    await this.put({
      key: this.listKey(principalId, surface),
      scopePrincipal: this.scopePrincipal(principalId),
      kind: "list",
      surface,
      threadId: "",
      updatedAt: Date.now(),
      value: items,
    });
  }

  async removeThreadFromList(principalId: string, surface: CachedThreadSurface, threadId: string): Promise<void> {
    const cached = await this.getList(principalId, surface);
    if (!cached) return;
    const items = cached.items.filter((item) => String(item.id || item.threadId || "") !== threadId);
    await this.setList(principalId, surface, items);
  }

  private async prune(principalId: string): Promise<void> {
    const database = await this.database();
    if (!database) return;
    try {
      const records = await requestResult(
        database.transaction(STORE_NAME, "readonly")
          .objectStore(STORE_NAME)
          .index("scopePrincipal")
          .getAll(this.scopePrincipal(principalId)),
      ) as CacheRecord[];
      const stale = records
        .filter((record) => record.kind === "thread")
        .sort((left, right) => right.updatedAt - left.updatedAt)
        .slice(MAX_THREADS_PER_ACCOUNT);
      if (!stale.length) return;
      const transaction = database.transaction(STORE_NAME, "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      for (const record of stale) {
        store.delete(record.key);
        this.memory.delete(record.key);
      }
      await transactionDone(transaction);
    } catch {
      // Storage quota pruning is best-effort and never blocks opening a thread.
    }
  }
}
